import type { FC } from 'react'
import { useLayoutEffect, useMemo } from 'react'

import { useOptionalShop } from '~/hooks/data/use-shop'
import type {
  CollectionSeoData,
  ProductSeoData,
  StoreSeoData,
} from '~/utils/seo-helpers'
import { generateStoreSeoData } from '~/utils/seo-helpers'

type SeoData = StoreSeoData | ProductSeoData | CollectionSeoData

// Function to transform _type properties back to @type for JSON-LD
const transformSeoDataForJsonLd = (
  seoData: SeoData
): Record<string, unknown> => {
  const transformObject = (obj: unknown): unknown => {
    if (obj === null || typeof obj !== 'object') {
      return obj
    }

    if (Array.isArray(obj)) {
      return obj.map(transformObject)
    }

    const transformed: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (key === '_type') {
        transformed['@type'] = value
      } else {
        transformed[key] = transformObject(value)
      }
    }
    return transformed
  }

  const transformedData = transformObject(seoData) as Record<string, unknown>

  return {
    '@context': 'https://schema.org',
    ...transformedData,
  }
}

export const Seo: FC<{ seoData?: SeoData | null }> = ({ seoData }) => {
  const shop = useOptionalShop()

  const storeSeoData = useMemo(() => {
    if (!shop) return null
    return generateStoreSeoData(shop)
  }, [shop])

  const jsonLdData = useMemo(() => {
    const data = seoData || storeSeoData
    if (!data) return null
    return transformSeoDataForJsonLd(data)
  }, [seoData, storeSeoData])

  useLayoutEffect(() => {
    if (!jsonLdData) return

    // Get or create the script element
    const scriptId = 'structured-data-script'
    let existingScript = document.getElementById(scriptId) as HTMLScriptElement

    if (!existingScript) {
      existingScript = document.createElement('script')
      existingScript.id = scriptId
      existingScript.type = 'application/ld+json'
      document.body.appendChild(existingScript)
    }

    // Update the script content
    existingScript.textContent = JSON.stringify(jsonLdData, null, 2)
  }, [jsonLdData])

  return null
}
